import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Typography } from '@/components/ui/typography'
import SameDayDeliveryAnnouncementSettings from '@/components/websites/SameDayDeliveryAnnouncementSettings'
import { Can, ReadOnly, useIsReadOnly } from '@/lib/casl'
import { useFormContext } from '@/lib/form-contexts'
import _get from 'lodash/get'
import {
  BookOpen,
  Clock,
  Images,
  Link,
  MapPin,
  Megaphone,
  MessageCircleHeart,
  Navigation,
  Pencil,
  Phone,
  PlusIcon,
  Star,
} from 'lucide-react'
import LogoDisplayForm from './LogoDisplayForm'
import NavItemsForm from './NavItemsForm'
import NavigationMenuEditor from './NavigationMenuEditor'
import {
  addTopMessageBarPageLink,
  createTopMessageBarNavItem,
  hasTopMessageBarLink,
  PAGE_ANNOUNCEMENT_LINK_OPTIONS,
} from './topMessageBarLinks'

const PREFIX = 'pages./.blocks.header'
const name = (str) => `${PREFIX}.${str}`
const banner = (str) => name(`banner.0.${str}`)
const navMenu = (str) => name(`navMenu.0.${str}`)

const HeaderSectionForm = ({
  showAnnouncementBar = true,
  showNavigation = true,
  showLogoDisplayControls = false,
  showLogoSizePreview = true,
  showSameDayDeliverySettings = false,
  showNavigationModeControls = true,
}) => {
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()
  const getBannerNavItems = () => {
    const items = form.getFieldValue(banner('nav_items'))
    return Array.isArray(items) ? items : []
  }

  const hasPageLink = (href) => hasTopMessageBarLink(getBannerNavItems(), href)

  const handleAddLink = () => {
    form.pushFieldValue(
      banner('nav_items'),
      createTopMessageBarNavItem({
        type: 'link',
        label: 'Tap to edit link',
      })
    )
  }
  const handleAddText = () => {
    form.pushFieldValue(
      banner('nav_items'),
      createTopMessageBarNavItem({
        type: 'text',
        label: 'Tap to edit text',
      })
    )
  }
  const handleAddBusinessInfo = (type) => {
    if (type === 'phone') {
      form.pushFieldValue(
        banner('nav_items'),
        createTopMessageBarNavItem({
          type: 'text',
          label: '{{location.phone}}',
          props: { showOnMobile: true },
        })
      )
    } else if (type === 'hours') {
      form.pushFieldValue(
        banner('nav_items'),
        createTopMessageBarNavItem({ type: 'text', label: '{{location.hours}}' })
      )
    } else if (type === 'address') {
      form.pushFieldValue(
        banner('nav_items'),
        createTopMessageBarNavItem({ type: 'text', label: '{{location.address}}' })
      )
    }
  }
  const handleAddPageLink = (page) => {
    const option = PAGE_ANNOUNCEMENT_LINK_OPTIONS[page]
    if (!option || hasPageLink(option.href)) {
      return
    }

    form.setFieldValue(banner('nav_items'), addTopMessageBarPageLink(getBannerNavItems(), option), {
      touch: true,
    })
  }

  return (
    <div className="flex flex-col gap-y-10 mb-4">
      {/* BANNER SECTION */}
      {showAnnouncementBar ? (
        <div>
          <div className="mb-4">
            <div className="inline-flex items-center gap-2">
              <Megaphone />
              <Typography variant="h4">Announcement Bar</Typography>
            </div>
            <div>
              <Typography variant="supporting">
                Share specials, deadlines, store hours, or helpful quick links.
              </Typography>
            </div>
          </div>
          <div className="ml-6">
            <div className="mb-8">
              <form.AppField name={banner('is_enabled')}>
                {(field) => <field.SwitchInput label="Show Announcement Bar" />}
              </form.AppField>
            </div>
            <form.Subscribe selector={(state) => _get(state.values, banner('is_enabled'))}>
              {(isEnabled) => (
                <ReadOnly isReadOnly={!isEnabled}>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-8">
                      <div className="w-full sm:w-auto">
                        <form.AppField name={banner('props.layout')}>
                          {(field) => (
                            <field.SelectInput
                              label="Item Placement"
                              className="w-full sm:max-w-60"
                              options={[
                                { id: 'left', name: 'Left' },
                                { id: 'right', name: 'Right' },
                                { id: 'center', name: 'Center' },
                                { id: 'spread', name: 'Spread' },
                                { id: 'split', name: 'Split Left/Right' },
                              ]}
                            />
                          )}
                        </form.AppField>
                      </div>
                      <div className="w-full sm:w-auto">
                        <DropdownMenu className="ml-auto">
                          <DropdownMenuTrigger asChild>
                            <Button className="w-full sm:w-auto" disabled={isReadOnly}>
                              Add Item <PlusIcon />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuGroup>
                              <DropdownMenuLabel>Pages</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => handleAddPageLink('aboutUs')}>
                                <BookOpen /> About Us
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddPageLink('gallery')}>
                                <Images /> Gallery
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddPageLink('contactUs')}>
                                <MessageCircleHeart /> Contact
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddPageLink('reviews')}>
                                <Star /> Reviews
                              </DropdownMenuItem>
                            </DropdownMenuGroup>
                            <DropdownMenuGroup>
                              <DropdownMenuLabel>Store Info</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => handleAddBusinessInfo('phone')}>
                                <Phone /> Phone
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddBusinessInfo('hours')}>
                                <Clock /> Today's Hours
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddBusinessInfo('address')}>
                                <MapPin /> Address
                              </DropdownMenuItem>
                            </DropdownMenuGroup>
                            <DropdownMenuGroup>
                              <DropdownMenuLabel>Custom Content</DropdownMenuLabel>
                              <DropdownMenuItem onClick={handleAddText}>
                                <Pencil /> Text
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={handleAddLink}>
                                <Link /> Link
                              </DropdownMenuItem>
                            </DropdownMenuGroup>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <div>
                      <form.Subscribe
                        selector={(state) => _get(state.values, banner('props.layout'))}
                      >
                        {(layout) => (
                          <form.AppField name={banner('nav_items')}>
                            {() => (
                              <NavItemsForm layout={layout} canDelete canEdit showLayoutLabel />
                            )}
                          </form.AppField>
                        )}
                      </form.Subscribe>
                    </div>
                  </div>
                </ReadOnly>
              )}
            </form.Subscribe>
          </div>
        </div>
      ) : null}
      {showLogoDisplayControls ? (
        <LogoDisplayForm showLogoSizePreview={showLogoSizePreview} />
      ) : null}
      {showNavigation ? (
        <div className="flex flex-col gap-4">
          {/*  NAVIGATION MENU SECTION */}
          <div>
            <div className="inline-flex items-center gap-2">
              <Navigation />
              <Typography variant="h4">Main Navigation</Typography>
            </div>
            <div>
              <div>
                <Typography variant="supporting">
                  Choose which links appear at the top of your website and in what order.
                </Typography>
              </div>
            </div>
          </div>
          <div className="@container ml-6">
            <div className="flex flex-col gap-8">
              <div>
                <form.AppField name={navMenu('is_enabled')}>
                  {(field) => <field.SwitchInput label="Show Main Navigation" />}
                </form.AppField>
              </div>
              <form.Subscribe selector={(state) => _get(state.values, navMenu('is_enabled'))}>
                {(isEnabled) => (
                  <ReadOnly isReadOnly={!isEnabled}>
                    <form.AppField name={navMenu('nav_items')}>
                      {() => (
                        <NavigationMenuEditor
                          modeFieldName={navMenu('props.itemsMode')}
                          showModeControls={showNavigationModeControls}
                        />
                      )}
                    </form.AppField>
                  </ReadOnly>
                )}
              </form.Subscribe>
            </div>
          </div>
        </div>
      ) : null}
      {showSameDayDeliverySettings ? (
        <Can do="view" on="stores">
          <Can do="edit" on="stores" passThrough>
            {(canEditStore) => <SameDayDeliveryAnnouncementSettings canEditStore={canEditStore} />}
          </Can>
        </Can>
      ) : null}
    </div>
  )
}

export default HeaderSectionForm
